/**
 * Renderer-only audio debug helpers: the report senders feeding the main
 * store, enumerateDevices routing snapshots, and the markdown support report.
 *
 * The cross-process contract (GlobalState keys, schemas, IPC event names,
 * master-flag helpers) lives in `@shared/audio-debug` — import it from there.
 */
import { GlobalEvents } from '@highlight/ipc'
import type { ChannelLabel } from './types'
import type { AudioDebugInfo } from '@shared/types/audio-debug-info'
import {
  AUDIO_DEBUG_REPORT_EVENT,
  type AudioDebugChannelLabel,
  type AudioDebugChannelState,
  type AudioDebugEvent,
  type AudioDebugReport,
  type AudioDebugRouting,
} from '@shared/audio-debug'

// ── Report senders ──────────────────────────────────────────────
//
// Targeted invokes into the main store — nothing fans out to other
// renderers. Fire-and-forget: a dropped report only means one stale
// tick on the overlay. Main-process producers must NOT use these
// (main can't invoke its own handlers); they call
// `applyAudioDebugReport` on `@main/utils/audio-debug-store` directly.

function sendAudioDebugReport(report: AudioDebugReport): void {
  void GlobalEvents.invoke(AUDIO_DEBUG_REPORT_EVENT, report).catch((err) => {
    console.warn('[audio-debug] report failed:', err)
  })
}

export function publishAudioDebugRouting(routing: AudioDebugRouting): void {
  sendAudioDebugReport({ kind: 'routing', routing })
}

/** Always-on by design: two low-cadence reports per diagnostics tick keep
 *  the store warm so the overlay never opens onto stale "inactive" cards. */
export function publishAudioDebugChannelState(state: AudioDebugChannelState): void {
  sendAudioDebugReport({ kind: 'channel', state })
}

/** Symmetric with the publisher above — both are un-gated so dispose
 *  ordering can never leave an orphaned state entry. */
export function clearAudioDebugChannelState(label: AudioDebugChannelLabel): void {
  sendAudioDebugReport({ kind: 'channel-clear', label })
}

/** Events fire only at discrete state changes; the store caps the log at
 *  `AUDIO_DEBUG_EVENT_LOG_MAX`. */
export function publishAudioDebugEvent(event: AudioDebugEvent): void {
  sendAudioDebugReport({ kind: 'event', event })
}

// ── Routing snapshot helpers ────────────────────────────────────

/**
 * Bluetooth label heuristic shared by routing diagnostics and the
 * HFP-avoidance mic picker. groupId tells us when default input/output
 * are the same physical device; the label check keeps the picker from
 * overriding wired devices or falling back to another Bluetooth headset.
 */
export function looksBluetoothLabel(label: string): boolean {
  return /\bbluetooth\b|airpods|powerbeats|\bbeats\b|\bhands[-\s]?free\b|\bhfp\b|\bhsp\b|\bbose\b|\bjabra\b|\bsennheiser\b|\bplantronics\b|\bsony\s+w[hf]|\bw[hf]-\d|galaxy\s+buds|pixel\s+buds/i.test(
    label,
  )
}

/**
 * Recognise the host machine's built-in microphone by label. macOS
 * presents these as "MacBook Pro Microphone", "MacBook Air Microphone",
 * "Mac mini Microphone", "iMac Microphone", "Mac Studio Microphone",
 * or "Built-in Microphone" on older Macs; Windows typically as
 * "Microphone Array" or a "(Realtek/Internal Microphone)" suffix.
 * Used by the HFP-avoidance picker to *prefer* the laptop's own mic
 * over Continuity Camera / iPhone / virtual mics when overriding a
 * Bluetooth default.
 */
export function looksBuiltInMicLabel(label: string): boolean {
  return /macbook|\bmac\s+mini\b|\bimac\b|\bmac\s+studio\b|built[-\s]?in|internal\s+microphone|microphone\s+array/i.test(
    label,
  )
}

/**
 * Devices we never want to silently auto-switch the mic *to*:
 *
 *   - Continuity Camera mics on iPhone / iPad (appear when the phone
 *     is unlocked nearby — almost always not what the user wants for
 *     a meeting on their laptop).
 *   - Virtual audio devices like BlackHole, Loopback Audio, Soundflower,
 *     VB-Cable: these route audio out of/into other apps and would
 *     loop a meeting's far-end audio back into the mic channel.
 *   - Conferencing-app virtual mics (Zoom, Microsoft Teams Audio
 *     Device, GoTo, Webex audio helper) — same routing footgun.
 *
 * Note this list is consulted *only* when proposing a non-Bluetooth
 * fallback. If the user has explicitly selected one of these as their
 * mic the service still uses it.
 */
export function looksUnwantedAutoMicLabel(label: string): boolean {
  return /iphone|ipad|continuity|blackhole|\bloopback\b|soundflower|\bvb[-\s]?cable\b|\bzoomaudiodevice\b|\bms[-\s]?teams\b|microsoft\s+teams\s+audio|webex\s+audio|goto\s+audio/i.test(
    label,
  )
}

/**
 * Snapshot the active audio routing as Chromium / the OS report it.
 * Returns a structured object suitable for both:
 *   - the existing one-line debug log (use `formatAudioRouting()`)
 *   - the cross-process audio-debug overlay (publish directly via
 *     `publishAudioDebugRouting()`)
 *
 * Diagnostic-only; never throws. Returns `null` on enumerateDevices
 * failure so the caller can log a fallback and skip the overlay
 * update without throwing into the audio acquire path.
 */
export async function snapshotAudioRouting(): Promise<AudioDebugRouting | null> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const outputs = devices.filter((d) => d.kind === 'audiooutput')
    const inputs = devices.filter((d) => d.kind === 'audioinput')

    const defaultOut = outputs.find((d) => d.deviceId === 'default')
    const defaultIn = inputs.find((d) => d.deviceId === 'default')

    const btOutputs = outputs
      .filter((d) => looksBluetoothLabel(d.label))
      .map((d) => ({ label: d.label || '(unlabeled)', groupId: d.groupId || '' }))
    const btInputs = inputs
      .filter((d) => looksBluetoothLabel(d.label))
      .map((d) => ({ label: d.label || '(unlabeled)', groupId: d.groupId || '' }))

    // HFP risk only matters when the default input is also the default
    // output: CATAP captures the default output, so non-default Bluetooth
    // outputs should not cause a mic override.
    const sharedGroupId = defaultIn?.groupId && defaultOut?.groupId === defaultIn.groupId
    const hfpRisk = !!sharedGroupId

    return {
      takenAtMs: Date.now(),
      defaultOutputLabel: defaultOut?.label || '(no default)',
      defaultOutputGroupId: defaultOut?.groupId || '',
      defaultInputLabel: defaultIn?.label || '(no default)',
      defaultInputGroupId: defaultIn?.groupId || '',
      bluetoothOutputs: btOutputs,
      bluetoothInputs: btInputs,
      hfpTriggerRisk: hfpRisk,
    }
  } catch (err) {
    console.warn('[AudioTranscriptionService] snapshotAudioRouting failed:', err)
    return null
  }
}

/** Render an `AudioDebugRouting` snapshot as a one-line log string.
 *  Kept compact so it fits the existing console.log style — the overlay
 *  reads the structured snapshot directly via GlobalState. */
export function formatAudioRouting(routing: AudioDebugRouting | null): string {
  if (routing === null) return '(enumerateDevices failed)'
  const fmt = (label: string, groupId: string): string => `"${label}" group=${groupId || '(none)'}`
  const fmtList = (devs: Array<{ label: string; groupId: string }>): string =>
    `[${devs.map((d) => fmt(d.label, d.groupId)).join(', ')}]`
  const parts: Array<string> = []
  parts.push(`output[default]=${fmt(routing.defaultOutputLabel, routing.defaultOutputGroupId)}`)
  parts.push(`input[default]=${fmt(routing.defaultInputLabel, routing.defaultInputGroupId)}`)
  if (routing.bluetoothOutputs.length > 0) parts.push(`bluetoothOutputs=${fmtList(routing.bluetoothOutputs)}`)
  if (routing.bluetoothInputs.length > 0) parts.push(`bluetoothInputs=${fmtList(routing.bluetoothInputs)}`)
  parts.push(`hfpTriggerRisk=${routing.hfpTriggerRisk ? 'TRUE' : 'false'}`)
  return parts.join(' | ')
}

/** Combined snapshot+publish helper. Used by both the diagnostic
 *  tick (every 5s when the overlay is enabled — keeps the routing
 *  card current as the OS default device shifts) and the master-flag
 *  enable handler (forces a fresh snapshot the moment the user opens
 *  the overlay mid-session). Skips publishing when enumerateDevices
 *  fails. */
export async function snapshotAndPublishAudioRouting(): Promise<void> {
  const routing = await snapshotAudioRouting()
  if (routing) publishAudioDebugRouting(routing)
}

// ── Markdown report (shareable support snapshot) ────────────────

/** Service-level context to layer into the markdown report. The
 *  fields are all optional so the formatter can run against just the
 *  audio-debug state, but populating them turns the report into a
 *  drop-in support attachment. */
export interface AudioDebugMarkdownMeta {
  /** Snapshot of `audio-transcription-service.status`. */
  serviceStatus?: string
  /** Current capture error published on
   *  `audio-transcription-service.captureError`. */
  captureError?: { channel: string; error: string } | null
  /** App release channel (e.g. `stable`, `beta`). */
  appChannel?: string
  /** App version string (e.g. `1.234.5`). */
  appVersion?: string
  /** OS platform (e.g. `darwin`, `win32`). */
  platform?: string
  /** Opaque device identifier so support can correlate with logs. */
  deviceId?: string
  /** Anonymous user id so support can correlate with Pylon tickets. */
  userId?: string
  /** Override the captured-at timestamp; defaults to `Date.now()`. */
  capturedAtMs?: number
}

export interface AudioDebugMarkdownInput {
  routing: AudioDebugRouting | undefined | null
  mic: AudioDebugChannelState | undefined | null
  sys: AudioDebugChannelState | undefined | null
  events: Array<AudioDebugEvent>
  meta?: AudioDebugMarkdownMeta
  /** Optional combined snapshot from `AudioNoteIpc.getAudioDebugInfo()`.
   *  Adds runtime / session / transcript-count / meeting context plus the
   *  per-channel WS reconnect history. Omitted when called from contexts
   *  that don't have IPC access (tests, browser shell). */
  audioInfo?: AudioDebugInfo | null
  /** Set when the caller TRIED to collect `audioInfo` over IPC but the
   *  call failed. The formatter inserts a prominent warning so the
   *  resulting report doesn't silently look identical to "IPC absent on
   *  purpose" — support otherwise couldn't tell whether the missing
   *  sections meant "no recording" or "renderer is wedged."  */
  audioInfoError?: string | null
}

/**
 * Render the live audio-debug surface as a single markdown document
 * suitable for pasting into a support ticket. Pure function: takes
 * the same snapshots the overlay reads from GlobalState and emits a
 * deterministic report. Sections with no data render as `_inactive_`
 * so the shape stays consistent across captures.
 */
export function formatAudioDebugMarkdown(input: AudioDebugMarkdownInput): string {
  const capturedAtMs = input.meta?.capturedAtMs ?? Date.now()
  const lines: Array<string> = []

  lines.push('# Highlight audio debug snapshot', '')
  lines.push(`- **Captured:** ${new Date(capturedAtMs).toISOString()}`)
  if (input.meta?.appVersion || input.meta?.appChannel) {
    const version = input.meta.appVersion ?? 'unknown'
    const channel = input.meta.appChannel ?? 'unknown'
    lines.push(`- **App:** ${version} (${channel})`)
  }
  if (input.meta?.platform) lines.push(`- **Platform:** ${input.meta.platform}`)
  if (input.meta?.deviceId) lines.push(`- **Device id:** \`${input.meta.deviceId}\``)
  if (input.meta?.userId) lines.push(`- **User id:** \`${input.meta.userId}\``)
  if (input.meta?.serviceStatus) lines.push(`- **Service status:** \`${input.meta.serviceStatus}\``)
  if (input.meta?.captureError) {
    lines.push(`- **Capture error (${input.meta.captureError.channel}):** ${input.meta.captureError.error}`)
  }
  lines.push('')

  if (input.audioInfoError) {
    // Loud banner so the missing Session / Meeting / Electron-runtime
    // sections below aren't mistaken for an idle service. Without this
    // the report looks identical to "no recording in progress" exactly
    // when the IPC wedge is the thing we want support to notice.
    lines.push(`> ⚠️ \`getAudioDebugInfo\` IPC failed: ${input.audioInfoError} — runtime sections below are missing.`)
    lines.push('')
  }
  if (input.audioInfo) lines.push(...formatAudioInfoSection(input.audioInfo))

  lines.push('## Routing')
  if (!input.routing) {
    lines.push('_no routing snapshot yet (acquire a capture)_', '')
  } else {
    const r = input.routing
    lines.push(`- **Default output:** ${r.defaultOutputLabel} (group \`${r.defaultOutputGroupId || '(none)'}\`)`)
    lines.push(`- **Default input:** ${r.defaultInputLabel} (group \`${r.defaultInputGroupId || '(none)'}\`)`)
    if (r.bluetoothOutputs.length > 0) {
      lines.push(`- **Bluetooth outputs:** ${r.bluetoothOutputs.map((d) => d.label).join(', ')}`)
    }
    if (r.bluetoothInputs.length > 0) {
      lines.push(`- **Bluetooth inputs:** ${r.bluetoothInputs.map((d) => d.label).join(', ')}`)
    }
    lines.push(`- **HFP trigger risk:** ${r.hfpTriggerRisk ? '**HIGH**' : 'low'}`)
    lines.push(
      `- **Snapshot taken:** ${formatRelMs(r.takenAtMs, capturedAtMs)} (${new Date(r.takenAtMs).toISOString()})`,
    )
    lines.push('')
  }

  for (const label of ['sys', 'mic'] as const) {
    const state = label === 'sys' ? input.sys : input.mic
    lines.push(...formatChannelMarkdown(label, state ?? null, capturedAtMs))
  }

  const events = input.events ?? []
  lines.push(`## Events (${events.length})`)
  if (events.length === 0) {
    lines.push('_no events_', '')
  } else {
    // Newest first matches the overlay's triage order.
    const sorted = [...events].reverse()
    for (const e of sorted) {
      const time = new Date(e.atMs).toISOString().slice(11, 23) // HH:MM:SS.mmm
      const channel = e.channel ?? 'svc'
      lines.push(`- \`${time}\` [${e.severity}] ${channel} — ${e.message}`)
    }
    lines.push('')
  }

  return lines.join('\n')
}

function formatChannelMarkdown(
  label: ChannelLabel,
  state: AudioDebugChannelState | null,
  capturedAtMs: number,
): Array<string> {
  const upper = label.toUpperCase()
  if (!state) return [`## Channel: ${upper}`, '_inactive_', '']

  const showMeasured = state.measuredSampleRate > 0
  const sampleRateLine = showMeasured
    ? `claimed ${state.claimedSampleRate} Hz, measured ${state.measuredSampleRate.toFixed(0)} Hz, target ${state.targetSampleRate} Hz`
    : `claimed ${state.claimedSampleRate} Hz, target ${state.targetSampleRate} Hz`

  return [
    `## Channel: ${upper} — ${state.phase}`,
    `- **Sample rate:** ${sampleRateLine}`,
    `- **Frames/s:** ${state.framesPerSecond.toFixed(1)} (total ${state.totalFrames})`,
    `- **Levels:** rms ${state.rmsMean.toFixed(4)}, peak ${state.peakAbs.toFixed(4)}`,
    `- **WS sent:** ${state.bytesSentKbps.toFixed(1)} KB/s · ${(state.bytesSentTotal / 1024).toFixed(0)} KB total`,
    `- **Buffered:** ${(state.bufferedBytes / 1024).toFixed(1)} KB`,
    `- **Transcripts:** ${state.transcriptsTotal} received (${state.wsMessagesTotal} WS msgs)`,
    `- **WS:** ${state.wsReadyState} ${state.serverReady ? '(ready)' : '(handshaking)'}`,
    `- **Track:** ${state.liveTrack ? 'live' : 'ended'}`,
    `- **Last event:** ${state.lastEvent ?? '—'}`,
    `- **Updated:** ${formatRelMs(state.takenAtMs, capturedAtMs)} (${new Date(state.takenAtMs).toISOString()})`,
    '',
  ]
}

function formatRelMs(atMs: number, nowMs: number): string {
  const dt = Math.max(0, nowMs - atMs)
  if (dt < 1000) return `${dt}ms ago`
  if (dt < 60_000) return `${Math.floor(dt / 1000)}s ago`
  if (dt < 3_600_000) return `${Math.floor(dt / 60_000)}m ago`
  return `${Math.floor(dt / 3_600_000)}h ago`
}

/** Render the `AudioDebugInfo` snapshot (collected over IPC from the main
 *  process) as a section of the support report. Covers session / runtime /
 *  transcript / meeting context and — when the electron runtime is active —
 *  per-channel WS reconnect history. */
function formatAudioInfoSection(info: AudioDebugInfo): Array<string> {
  const out: Array<string> = ['## Session']
  out.push(`- **Runtime:** ${info.runtime ?? '—'}`)
  out.push(`- **Status:** ${info.status ?? '—'}`)
  if (info.appVersion || info.platform) {
    const ver = info.appVersion ?? 'unknown'
    const plat = info.platform ?? 'unknown'
    const channel = info.appChannel ? ` (${info.appChannel})` : ''
    out.push(`- **App:** ${ver}${channel} on ${plat}`)
  }
  if (info.activeNoteId) out.push(`- **Active note:** \`${info.activeNoteId}\``)
  if (info.audioSessionId) out.push(`- **Audio session id:** \`${info.audioSessionId}\``)
  if (info.startTime) {
    const len = info.sessionLengthSeconds != null ? ` (${info.sessionLengthSeconds}s elapsed)` : ''
    out.push(`- **Started:** ${info.startTime}${len}`)
  }
  out.push(
    `- **Transcript entries:** processed=${info.transcriptCounts.processed}, provider=${info.transcriptCounts.provider}, merged=${info.transcriptCounts.merged}`,
  )
  out.push('')

  if (info.meeting) {
    out.push('## Meeting')
    if (info.meeting.appName) out.push(`- **App:** ${info.meeting.appName}`)
    if (info.meeting.title) out.push(`- **Title:** ${info.meeting.title}`)
    if (info.meeting.url) out.push(`- **URL:** ${info.meeting.url}`)
    out.push(`- **From calendar:** ${info.meeting.isScheduled ? 'yes' : 'no'}`)
    out.push('')
  }

  if (info.recall) {
    out.push('## Recall runtime')
    if (info.recall.windowId) out.push(`- **Window id:** \`${info.recall.windowId}\``)
    if (info.recall.recallRecordingId) out.push(`- **Recording id:** \`${info.recall.recallRecordingId}\``)
    if (info.recall.audioSessionId) out.push(`- **Audio session id:** \`${info.recall.audioSessionId}\``)
    out.push('')
  }

  if (info.electronRuntime) {
    out.push('## Electron audio runtime')
    if (info.electronRuntime.unavailableReason) {
      // The runtime window exists but the snapshot collection failed.
      // Call this out at the top of the section so support doesn't
      // mistake the empty channel summaries for an idle runtime.
      out.push(`> ⚠️ Snapshot unavailable: ${info.electronRuntime.unavailableReason}`)
      out.push('')
    }
    out.push(`- **Status:** \`${info.electronRuntime.status}\``)
    out.push(`- **Paused:** ${info.electronRuntime.isPaused ? 'yes' : 'no'}`)
    if (info.electronRuntime.audioSessionId) {
      out.push(`- **Audio session id:** \`${info.electronRuntime.audioSessionId}\``)
    }
    if (info.electronRuntime.sessionStartTime > 0) {
      out.push(`- **Service start:** ${new Date(info.electronRuntime.sessionStartTime).toISOString()}`)
    }
    out.push('')
    for (const [label, ch] of [
      ['MIC', info.electronRuntime.micChannel] as const,
      ['SYS', info.electronRuntime.systemChannel] as const,
    ]) {
      out.push(`### ${label} channel`)
      if (!ch.present) {
        out.push('_not active_', '')
        continue
      }
      out.push(`- **Track:** ${ch.hasLiveTrack ? 'live' : 'ended'}`)
      out.push(
        `- **WS:** ${ch.wsConnected ? 'connected' : 'disconnected'} (readyState=${ch.wsReadyState ?? 'null'}, serverReady=${ch.serverReady})`,
      )
      out.push(`- **Reconnects:** ${ch.reconnectAttempts} (next backoff ${ch.reconnectDelayMs}ms)`)
      out.push(`- **PCM buffered:** ${ch.pcmBufferBytes} bytes`)
      if (ch.disconnectedAt !== null) {
        out.push(`- **Disconnected at:** ${new Date(ch.disconnectedAt).toISOString()}`)
      }
      if (ch.wsSessionStartedAt > 0) {
        out.push(`- **WS session start:** ${new Date(ch.wsSessionStartedAt).toISOString()}`)
      }
      out.push(`- **Speaker offset:** ${ch.speakerIndexOffset} (max seen ${ch.maxSpeakerIndexSeen})`)
      out.push('')
    }
  }
  return out
}
